import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
  Settings, Plus, Edit, Trash2, ChevronLeft, ChevronRight,
  Shield, Building2, Copy, ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_GROUPS = [
  {
    key: 'competitions',
    labelKey: 'moduleGroupCompetitions',
    modules: [
      { id: 'competitions', labelKey: 'moduleCompetitions' },
      { id: 'registrations', labelKey: 'moduleRegistrations' },
      { id: 'live-events', labelKey: 'moduleLiveEvents' },
      { id: 'event-results', labelKey: 'moduleEventResults' },
    ]
  },
  {
    key: 'freedivers',
    labelKey: 'moduleGroupFreedivers',
    modules: [
      { id: 'freedivers', labelKey: 'moduleFreedivers' },
      { id: 'follow-up', labelKey: 'moduleFollowUp' },
      { id: 'performance-comparison', labelKey: 'modulePerformanceComparison' },
    ]
  },
  {
    key: 'training',
    labelKey: 'moduleGroupTraining',
    modules: [
      { id: 'training-sessions', labelKey: 'moduleTrainingSessions' },
      { id: 'exercise-library', labelKey: 'moduleExerciseLibrary' },
      { id: 'session-builder', labelKey: 'moduleSessionBuilder' },
      { id: 'apnea-tables', labelKey: 'moduleApneaTables' },
    ]
  },
  {
    key: 'management',
    labelKey: 'moduleGroupManagement',
    modules: [
      { id: 'organizations', labelKey: 'moduleOrganizations' },
      { id: 'aquatic-infrastructure', labelKey: 'moduleAquaticInfrastructure' },
    ]
  },
];

const ALL_MODULE_IDS = MODULE_GROUPS.flatMap(g => g.modules.map(m => m.id));
const USERS_PER_PAGE = 10;

const UserManagementModal = ({ open, onOpenChange, authToken, t, organizations = [] }) => {
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitError, setSubmitError] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '', password: '', display_name: '', email: '',
    role: 'user', language: 'en',
    allowed_modules: [], assigned_organization_ids: []
  });
  const copyRightsSelectRef = useRef(null);

  const fetchUsers = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [authToken]);

  useEffect(() => {
    if (open && authToken) {
      fetchUsers();
      setView('list');
    }
  }, [open, authToken, fetchUsers]);

  const totalPages = Math.max(1, Math.ceil(users.length / USERS_PER_PAGE));
  const paginatedUsers = users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  const resetForm = () => {
    setUserForm({
      username: '', password: '', display_name: '', email: '',
      role: 'user', language: 'en',
      allowed_modules: [], assigned_organization_ids: []
    });
    setEditingUser(null);
    setSubmitError('');
  };

  const goToList = () => {
    resetForm();
    setView('list');
  };

  const openCreate = () => {
    resetForm();
    setView('form');
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setSubmitError('');
    setUserForm({
      username: user.username || '',
      password: '',
      display_name: user.display_name || '',
      email: user.email || '',
      role: user.role || 'user',
      language: user.language || 'en',
      allowed_modules: Array.isArray(user.allowed_modules) ? [...user.allowed_modules] : [],
      assigned_organization_ids: Array.isArray(user.assigned_organization_ids) ? [...user.assigned_organization_ids] : []
    });
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    try {
      if (editingUser) {
        const updateData = {
          display_name: userForm.display_name,
          email: userForm.email || null,
          role: userForm.role,
          language: userForm.language,
          allowed_modules: userForm.allowed_modules,
          assigned_organization_ids: userForm.assigned_organization_ids,
        };
        if (userForm.password) updateData.password = userForm.password;
        await axios.put(`${API}/api/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      } else {
        await axios.post(`${API}/api/users`, {
          username: userForm.username,
          password: userForm.password,
          display_name: userForm.display_name,
          email: userForm.email || null,
          role: userForm.role,
          language: userForm.language,
          allowed_modules: userForm.allowed_modules,
          assigned_organization_ids: userForm.assigned_organization_ids,
        }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      }
      await fetchUsers();
      goToList();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error';
      setSubmitError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setDeletingUserId(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
      setDeletingUserId(null);
    }
  };

  const toggleModule = (moduleId) => {
    setUserForm(prev => {
      const current = prev.allowed_modules || [];
      return { ...prev, allowed_modules: current.includes(moduleId) ? current.filter(m => m !== moduleId) : [...current, moduleId] };
    });
  };

  const toggleGroupModules = (group) => {
    const groupIds = group.modules.map(m => m.id);
    const allSelected = groupIds.every(id => (userForm.allowed_modules || []).includes(id));
    setUserForm(prev => {
      const current = prev.allowed_modules || [];
      return { ...prev, allowed_modules: allSelected ? current.filter(m => !groupIds.includes(m)) : [...new Set([...current, ...groupIds])] };
    });
  };

  const toggleAllModules = () => {
    const allSelected = ALL_MODULE_IDS.every(id => (userForm.allowed_modules || []).includes(id));
    setUserForm(prev => ({ ...prev, allowed_modules: allSelected ? [] : [...ALL_MODULE_IDS] }));
  };

  const toggleOrg = (orgId) => {
    setUserForm(prev => {
      const current = prev.assigned_organization_ids || [];
      return { ...prev, assigned_organization_ids: current.includes(orgId) ? current.filter(id => id !== orgId) : [...current, orgId] };
    });
  };

  const copyRightsFromUser = (sourceUserId) => {
    if (!sourceUserId) return;
    const src = users.find(u => u.id === sourceUserId);
    if (!src) return;
    setUserForm(prev => ({
      ...prev,
      allowed_modules: Array.isArray(src.allowed_modules) ? [...src.allowed_modules] : [],
      assigned_organization_ids: Array.isArray(src.assigned_organization_ids) ? [...src.assigned_organization_ids] : []
    }));
  };

  const isAdmin = userForm.role === 'admin';
  const copyableUsers = users.filter(u => u.role !== 'admin' && (!editingUser || u.id !== editingUser.id));

  const handleModalClose = (v) => {
    if (!v) {
      resetForm();
      setView('list');
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="user-management-modal">
        {view === 'list' ? (
          <>
            {/* === LIST VIEW === */}
            <DialogHeader>
              <DialogTitle className="text-blue-200 flex items-center">
                <Settings size={24} className="mr-2 text-teal-400" />
                {t('userManagementTitle')}
              </DialogTitle>
              <DialogDescription className="text-blue-300">
                {t('manageUserAccounts') || 'Manage user accounts and permissions.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-blue-300 text-sm">{users.length} {t('user')}(s)</span>
                <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="create-user-btn">
                  <Plus size={16} className="mr-2" />
                  {t('createNewUser')}
                </Button>
              </div>

              <div className="bg-blue-800/30 rounded-lg overflow-hidden">
                <table className="w-full" data-testid="users-table">
                  <thead className="bg-blue-900/50">
                    <tr>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('username')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('displayName')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('role')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('allowedModules')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('assignedOrganizations')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('accountStatus')}</th>
                      <th className="px-3 py-3 text-left text-blue-200 font-semibold text-sm">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => (
                      <tr key={user.id} className="border-t border-blue-700/30 hover:bg-blue-800/20">
                        <td className="px-3 py-2 text-white text-sm">{user.username}</td>
                        <td className="px-3 py-2 text-blue-200 text-sm">{user.display_name}</td>
                        <td className="px-3 py-2">
                          <Badge className={user.role === 'admin' ? 'bg-teal-700/50 text-teal-200' : 'bg-blue-700/50 text-blue-200'}>
                            {user.role === 'admin' ? t('admin') : user.role === 'viewer' ? t('viewer') : t('user')}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {user.role === 'admin' ? (
                            <span className="text-teal-300 text-xs italic">{t('accessAllModules')}</span>
                          ) : (user.allowed_modules || []).length > 0 ? (
                            <span className="text-blue-300 text-xs">{(user.allowed_modules || []).length} module(s)</span>
                          ) : (
                            <span className="text-red-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {user.role === 'admin' ? (
                            <span className="text-teal-300 text-xs italic">{t('accessAllOrgs')}</span>
                          ) : (user.assigned_organization_ids || []).length > 0 ? (
                            <span className="text-blue-300 text-xs">
                              {(user.assigned_organization_ids || []).map(oid => {
                                const org = organizations.find(o => o.id === oid);
                                return org ? org.name : oid;
                              }).join(', ')}
                            </span>
                          ) : (
                            <span className="text-orange-400 text-xs">{t('noOrganizationAssigned')}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={user.is_active !== false ? 'bg-green-700/50 text-green-200' : 'bg-red-700/50 text-red-200'}>
                            {user.is_active !== false ? t('active') : t('inactive')}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center space-x-1">
                            {deletingUserId === user.id ? (
                              <>
                                <span className="text-red-300 text-xs mr-1">{t('confirmDeleteUser') || 'Supprimer ?'}</span>
                                <Button size="sm" variant="ghost" onClick={() => handleDelete(user.id)} className="text-red-400 hover:bg-red-700/50 h-7 px-2 text-xs" data-testid={`confirm-delete-${user.username}`}>
                                  {t('yes') || 'Oui'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeletingUserId(null)} className="text-blue-300 hover:bg-blue-700/50 h-7 px-2 text-xs" data-testid={`cancel-delete-${user.username}`}>
                                  {t('no') || 'Non'}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(user)} className="text-blue-300 hover:bg-blue-700/50 h-8 w-8 p-0" data-testid={`edit-user-${user.username}`}>
                                  <Edit size={14} />
                                </Button>
                                {user.username !== 'ACM-Admin' && (
                                  <Button size="sm" variant="ghost" onClick={() => setDeletingUserId(user.id)} className="text-red-300 hover:bg-red-700/50 h-8 w-8 p-0" data-testid={`delete-user-${user.username}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-3 pt-2" data-testid="users-pagination">
                  <Button size="sm" variant="ghost" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="text-blue-200 h-8 w-8 p-0">
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-blue-300 text-sm">{t('page')} {currentPage} {t('of')} {totalPages}</span>
                  <Button size="sm" variant="ghost" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="text-blue-200 h-8 w-8 p-0">
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* === FORM VIEW === */}
            <DialogHeader>
              <DialogTitle className="text-blue-200 flex items-center">
                <Button type="button" variant="ghost" onClick={goToList} className="text-blue-300 hover:text-white mr-2 h-8 w-8 p-0">
                  <ArrowLeft size={18} />
                </Button>
                {editingUser ? t('editUserTitle') : t('createNewUser')}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5">
              {submitError && (
                <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 text-red-200 text-sm">{submitError}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-blue-200 text-sm">{t('username')} *</Label>
                  <Input value={userForm.username} onChange={(e) => setUserForm({...userForm, username: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white mt-1" disabled={!!editingUser} required={!editingUser} data-testid="user-form-username" />
                </div>
                <div>
                  <Label className="text-blue-200 text-sm">{editingUser ? (t('newPassword') || 'New Password (blank = keep)') : t('password') + ' *'}</Label>
                  <Input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white mt-1" required={!editingUser} data-testid="user-form-password" />
                </div>
                <div>
                  <Label className="text-blue-200 text-sm">{t('displayName')} *</Label>
                  <Input value={userForm.display_name} onChange={(e) => setUserForm({...userForm, display_name: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white mt-1" required data-testid="user-form-display-name" />
                </div>
                <div>
                  <Label className="text-blue-200 text-sm">{t('email')}</Label>
                  <Input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white mt-1" data-testid="user-form-email" />
                </div>
                <div>
                  <Label className="text-blue-200 text-sm">{t('role')} *</Label>
                  <Select value={userForm.role} onValueChange={(value) => setUserForm({...userForm, role: value})}>
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1" data-testid="user-form-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      <SelectItem value="viewer">{t('viewer')}</SelectItem>
                      <SelectItem value="user">{t('user')}</SelectItem>
                      <SelectItem value="admin">{t('admin')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-blue-200 text-sm">{t('preferredLanguage')}</Label>
                  <Select value={userForm.language || 'en'} onValueChange={(value) => setUserForm({...userForm, language: value})}>
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1" data-testid="user-form-language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      <SelectItem value="en">EN English</SelectItem>
                      <SelectItem value="fr">FR Francais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Module Permissions */}
              <div className="bg-blue-800/30 rounded-lg p-4" data-testid="module-permissions-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-teal-300 font-semibold flex items-center text-sm">
                    <Shield size={16} className="mr-2" />
                    {t('allowedModules')}
                  </h3>
                  {!isAdmin && (
                    <Button type="button" size="sm" variant="ghost" onClick={toggleAllModules} className="text-blue-300 hover:text-white text-xs h-7">
                      {ALL_MODULE_IDS.every(id => (userForm.allowed_modules || []).includes(id)) ? t('deselectAll') : t('selectAll')}
                    </Button>
                  )}
                </div>

                {/* Copy rights from another user */}
                {!isAdmin && copyableUsers.length > 0 && (
                  <div className="mb-4 flex items-center gap-3 bg-blue-900/40 rounded-md p-3">
                    <Copy size={16} className="text-teal-400 shrink-0" />
                    <Label className="text-blue-200 text-sm whitespace-nowrap">{t('copyRightsFrom')}</Label>
                    <select
                      ref={copyRightsSelectRef}
                      defaultValue=""
                      onChange={(e) => { 
                        const selectedId = e.target.value;
                        if (selectedId) {
                          copyRightsFromUser(selectedId);
                        }
                      }}
                      className="flex-1 bg-blue-800/50 border border-blue-600 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                      data-testid="copy-rights-select"
                    >
                      <option value="">{t('selectUser')}</option>
                      {copyableUsers.map(u => (
                        <option key={u.id} value={u.id} className="bg-blue-800 text-white">
                          {u.display_name || u.username} ({(u.allowed_modules || []).length} modules)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isAdmin ? (
                  <p className="text-teal-300/70 text-sm italic">{t('accessAllModules')}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {MODULE_GROUPS.map((group) => {
                      const groupIds = group.modules.map(m => m.id);
                      const allGroupSelected = groupIds.every(id => (userForm.allowed_modules || []).includes(id));
                      return (
                        <div key={group.key} className="bg-blue-900/30 rounded-md p-3">
                          <div className="flex items-center gap-2 mb-2 cursor-pointer">
                            <Checkbox 
                              checked={allGroupSelected} 
                              onCheckedChange={() => toggleGroupModules(group)}
                              className="border-blue-400 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600" 
                              data-testid={`group-checkbox-${group.key}`} 
                            />
                            <span className="text-blue-100 font-medium text-sm" onClick={() => toggleGroupModules(group)}>{t(group.labelKey)}</span>
                          </div>
                          <div className="ml-6 space-y-1.5">
                            {group.modules.map((mod) => (
                              <div key={mod.id} className="flex items-center gap-2 cursor-pointer">
                                <Checkbox 
                                  checked={(userForm.allowed_modules || []).includes(mod.id)} 
                                  onCheckedChange={() => toggleModule(mod.id)}
                                  className="border-blue-500 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500" 
                                  data-testid={`module-checkbox-${mod.id}`} 
                                />
                                <span className="text-blue-200 text-sm" onClick={() => toggleModule(mod.id)}>{t(mod.labelKey)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Organization Assignment */}
              <div className="bg-blue-800/30 rounded-lg p-4" data-testid="org-assignment-section">
                <h3 className="text-teal-300 font-semibold flex items-center text-sm mb-3">
                  <Building2 size={16} className="mr-2" />
                  {t('assignedOrganizations')}
                </h3>
                {isAdmin ? (
                  <p className="text-teal-300/70 text-sm italic">{t('accessAllOrgs')}</p>
                ) : organizations.length === 0 ? (
                  <p className="text-blue-400 text-sm italic">{t('noOrganizations') || 'No organizations available'}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {organizations.map((org) => (
                      <div key={org.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-blue-700/30">
                        <Checkbox 
                          checked={(userForm.assigned_organization_ids || []).includes(org.id)} 
                          onCheckedChange={() => toggleOrg(org.id)}
                          className="border-blue-500 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500" 
                          data-testid={`org-checkbox-${org.id}`} 
                        />
                        <span className="text-blue-200 text-sm" onClick={() => toggleOrg(org.id)}>{org.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={goToList} className="border-blue-500 text-blue-200">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="user-form-submit">
                  {editingUser ? t('update') : t('create')}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementModal;
